<template>
	<GbGuideArticle nav-page="blogs">
		<h1><strong>GB WhatsApp Chat Privacy Guide</strong></h1>
		<p>Are you tired of people knowing exactly when you are online, or getting upset when you read their messages but do not reply immediately? Taking control of your digital footprint is the main reason people switch to <strong>GB WhatsApp</strong>.</p>
		<p>The official app gives you very basic privacy tools, but the <strong>GB WhatsApp mod</strong> takes it to a whole new level. You can freeze your online status, hide your typing indicators, and even lock specific chats behind a password.</p>
		<p>This guide will show you exactly how to configure these hidden controls. By the end of this page, you will be a ghost online. For more advanced tweaks, check out our <a href="/gb-whatsapp-advanced-settings-guide/">GB WhatsApp advanced settings guide</a>, or return to the <a href="/">GB WhatsApp</a> home page for other tutorials.</p>

		<h2><strong>Privacy Features Overview</strong></h2>
		<p>Before we dive into the steps, let's look at what you can actually do with the privacy menu in this app. The controls are split into two main categories: Global Privacy (affects everyone) and Custom Privacy (affects specific contacts).</p>
		<ul>
			<li><strong>Global Privacy:</strong> Found under GB Settings > Privacy and Security. These settings apply to all your contacts and groups by default.</li>
			<li><strong>Custom Privacy:</strong> Accessed by tapping on a specific friend's name in their chat. This lets you apply strict rules (like hiding blue ticks) to just one person without affecting the rest of your contacts.</li>
		</ul>
		<p>We recommend setting up your global rules first, and then using custom privacy for your boss or nosy relatives.</p>

		<h2><strong>Hide Online Status</strong></h2>
		<p>The most popular feature is the ability to hide your "Online" tag. When you enable this, you can chat, read messages, and browse statuses, but everyone else will see your last active time as frozen.</p>
		<ol>
			<li>Tap the three dots in the top right corner and open <strong>GB Settings</strong>.</li>
			<li>Tap on <strong>Privacy and Security</strong>.</li>
			<li>Toggle on <strong>Freeze Last Seen</strong>.</li>
			<li>Restart the <strong>GB WhatsApp app</strong> for the changes to take effect.</li>
		</ol>
		<p>Note: Unlike the official app, freezing your last seen here still allows you to see other people's online status!</p>

		<h3><strong>Hide Typing Status</strong></h3>
		<p>Nothing is more stressful than someone watching the "typing..." indicator while you try to formulate the perfect reply. You can turn this off entirely.</p>
		<ul>
			<li>Go to Privacy and Security > Contacts.</li>
			<li>Check the box for <strong>Hide typing...</strong></li>
			<li>You can also check <strong>Hide recording...</strong> to hide the microphone indicator when you are recording a voice note.</li>
		</ul>

		<h3><strong>Hide Blue Ticks</strong></h3>
		<p>Want to read messages without the sender knowing? Hiding the blue ticks (read receipts) is incredibly easy in <strong>GB WhatsApp</strong>.</p>
		<ul>
			<li>In the Privacy and Security menu, tap on Contacts.</li>
			<li>Check <strong>Hide Blue Ticks</strong>.</li>
			<li><strong>Pro Tip:</strong> You can also select "Show Blue Ticks after reply". This means the sender will only see the blue ticks after you actually type and send a response back to them.</li>
		</ul>

		<h2><strong>Chat Lock Settings</strong></h2>
		<p>Sometimes handing your phone to a friend to show them a picture turns into a privacy nightmare if they open your chats. The built-in chat lock prevents this.</p>
		<ol>
			<li>Open the specific conversation you want to protect.</li>
			<li>Tap the contact's name at the top to open their profile info.</li>
			<li>Scroll down and tap on <strong>Security</strong> or <strong>Chat Lock</strong>.</li>
			<li>Choose your preferred lock method: Pattern, PIN, or Fingerprint.</li>
			<li>Set up your password. Now, whenever someone tries to open this specific chat, they will hit a security wall.</li>
		</ol>

		<h2><strong>Advanced Privacy Controls</strong></h2>
		<p>If you want to go full stealth mode, explore these extra toggles in the <strong>GB WhatsApp settings</strong> menu:</p>
		<ul>
			<li><strong>Anti-Delete Messages:</strong> Prevents people from deleting messages they sent to you. You will always have the receipts.</li>
			<li><strong>Hide View Status:</strong> Watch your friends' 24-hour status updates without your name appearing in their "Viewed By" list.</li>
			<li><strong>Disable Forwarded Tag:</strong> When you forward a funny message or video, the app removes the "Forwarded" label, making it look like you typed or uploaded it yourself.</li>
			<li><strong>Call Privacy:</strong> Go to Privacy and Security > Who can call me. You can block specific people from calling you via WhatsApp without actually blocking them from sending text messages.</li>
		</ul>

		<h2><strong>Best Privacy Practices</strong></h2>
		<p>While having all these features is amazing, using them recklessly can sometimes cause glitches or make your friends suspicious.</p>
		<ul>
			<li><strong>Do Not Hide Second Tick:</strong> Hiding the second tick (delivered tick) makes it look like your phone is off. However, this feature is known to cause message delivery delays. We highly recommend leaving the second tick visible and only hiding the blue ticks.</li>
			<li><strong>Test Custom Privacy:</strong> If you apply custom privacy to a specific contact, double-check it. Sometimes overlapping global and custom rules can cause confusion.</li>
			<li><strong>Respect Others:</strong> Remember that while you are hiding your footprint, your friends might be doing the same. Do not stress if someone's last seen hasn't updated in days.</li>
		</ul>
		<p>By mastering these controls, you can finally use <strong>GB WhatsApp</strong> on your own terms, free from social pressure and unwanted surveillance.</p>
	</GbGuideArticle>
</template>

<script>
import GbGuideArticle from '@/components/GbGuideArticle.vue';

export default {
	name: 'GuideChatPrivacy',
	components: { GbGuideArticle },
};
</script>
